import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CopyButton } from '../copy.js'
import { api, ApiError } from '../api.js'

type Kind = 'people' | 'projects' | 'tasks'
interface RowResult { line: number; action: 'create' | 'skip' | 'error'; key: string; messages: string[] }
interface Preview { kind: Kind; headers: string[]; required: string[]; rows: RowResult[]; creatable: number; skipped: number; errors: number }
interface Commit { created: number; skipped: number; invites: { email: string; acceptUrl: string }[]; details: RowResult[] }

/** Admin → Import: paste or upload a CSV, preview every row, commit all-or-nothing. */
export function ImportPage() {
  const cols = useQuery({ queryKey: ['import-columns'], queryFn: () => api<{ columns: Record<Kind, string[]> }>('/import/columns') })
  const [kind, setKind] = useState<Kind>('people')
  const [csv, setCsv] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [result, setResult] = useState<Commit | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const pv = useMutation({ mutationFn: () => api<Preview>('/import/preview', { method: 'POST', json: { kind, csv } }), onSuccess: (r) => { setPreview(r); setResult(null) }, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Preview failed') })
  const cm = useMutation({ mutationFn: () => api<Commit>('/import/commit', { method: 'POST', json: { kind, csv } }), onSuccess: (r) => { setResult(r); setPreview(null) }, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Import failed') })
  const onFile = (f: File | undefined) => { if (!f) return; const rd = new FileReader(); rd.onload = () => setCsv(String(rd.result ?? '')); rd.readAsText(f) }
  const columns = cols.data?.columns[kind] ?? []
  return (
    <>
      <h1>{tr("Import")}</h1>
      <ul className="help muted">
        <li>{tr("Bulk-load people, projects, and an opening task backlog from CSV files.")}</li>
        <li><strong>{tr("Preview")}</strong> shows exactly what each row will do before anything is written.</li>
        <li><strong>{tr("Commit")}</strong> is all-or-nothing; re-running the same file is safe (existing rows are skipped).</li>
        <li>{tr("Import in order: people → projects → tasks.")}</li>
      </ul>
      <section className="card stack">
        <div className="row">
          <label className="inline">{tr("Kind")} <select value={kind} onChange={(e) => { setKind(e.target.value as Kind); setPreview(null); setResult(null) }}><option value="people">{tr("People")}</option><option value="projects">{tr("Projects")}</option><option value="tasks">{tr("Tasks")}</option></select></label>
          <label className="inline small">{tr("Upload")} <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} /></label>
          <button className="link small" onClick={() => setCsv(columns.join(',') + '\n')}>Insert header row</button>
        </div>
        <p className="small muted">Columns (first two required{kind === 'projects' ? ', timezone required' : ''}): <code>{columns.join(', ')}</code>. {kind === 'people' && 'People get an invite link to set a password; Google Workspace users can just sign in.'}{kind === 'projects' && 'member_emails is ;-separated; admin_email becomes the project administrator (else you do).'}{kind === 'tasks' && 'parent_title nests under a task in the same file or already in the project; status must be one of the project’s statuses (default: first “to do”).'}</p>
        <textarea rows={10} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={tr("Paste CSV here…")} style={{ fontFamily: 'ui-monospace, monospace', fontSize: '.85rem' }} />
        <div className="row">
          <button className="button" onClick={() => { setErr(null); pv.mutate() }} disabled={!csv.trim() || pv.isPending}>Preview</button>
          {preview && preview.errors === 0 && preview.creatable > 0 && <button className="button primary" onClick={() => { setErr(null); cm.mutate() }} disabled={cm.isPending}>Import {preview.creatable} row{preview.creatable === 1 ? '' : 's'}</button>}
        </div>
        {err && <p className="bad">{err}</p>}
      </section>
      {preview && (
        <section className="card stack">
          <h2>Preview · {preview.creatable} to create · {preview.skipped} skipped · <span className={preview.errors ? 'bad' : ''}>{preview.errors} errors</span></h2>
          {preview.errors > 0 && <p className="bad small">Fix the errors and preview again; nothing is imported until every row is valid.</p>}
          <RowsTable rows={preview.rows} />
        </section>
      )}
      {result && (
        <section className="card stack">
          <h2 className="good">Imported {result.created} · skipped {result.skipped}</h2>
          {result.invites.length > 0 && (
            <>
              <p className="small">Invite links for the new people (each shown once — send them on; they expire in 14 days):</p>
              <table className="table small"><tbody>{result.invites.map((i) => <tr key={i.email}><td>{i.email}</td><td><code className="wrap">{i.acceptUrl}</code></td></tr>)}</tbody></table>
              <CopyButton text={result.invites.map((i) => `${i.email}\t${i.acceptUrl}`).join('\n')} label={tr("Copy all")} tip="Copy every email + invite link as tab-separated lines" className="link" />
            </>
          )}
          <RowsTable rows={result.details} />
        </section>
      )}
    </>
  )
}

function RowsTable({ rows }: { rows: RowResult[] }) {
  return (
    <table className="table small">
      <thead><tr><th>{tr("Line")}</th><th>{tr("Key")}</th><th>{tr("Action")}</th><th>{tr("Notes")}</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.line}><td className="mono">{r.line}</td><td>{r.key}</td><td className={r.action === 'error' ? 'bad' : r.action === 'skip' ? 'muted' : 'good'}>{r.action}</td><td>{r.messages.join('; ')}</td></tr>)}</tbody>
    </table>
  )
}
